import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCheckInTote } from '../hooks/backend/scanPage'
import { useDeleteTote } from '../hooks/backend/totesPage'
import { useGetTotesCached } from '../hooks/cachedQueries'
import { playSound } from './ui/scanSounds'
import { toast } from '../lib/shadcn/sonner'
import { useOnRestored, useUndoToast } from '../hooks/useUndoToast'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Badge } from '../lib/shadcn/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../lib/shadcn/alert-dialog'
import { RefreshCw, X, PackageOpen, Trash2 } from 'lucide-react'

interface Tote {
  id: number
  customer_id: string | null
  customer: string | null
  shipping_address: string | null
  order_id: string | null
  salesorder_number: string | null
  last_modified: string | null
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString()
}

export default function TotesPage() {
  const getTotes = useGetTotesCached()
  const checkInTote = useCheckInTote()
  const deleteTote = useDeleteTote()
  const showUndo = useUndoToast()
  const [searchParams, setSearchParams] = useSearchParams()

  const [scanValue, setScanValue] = useState('')
  const [toteFilter, setToteFilter] = useState('')
  const [customerFilter, setCustomerFilter] = useState<string | null>(
    searchParams.get('customer_id')
  )
  const [sorting, setSorting] = useState<SortingState>([])
  const [pendingCheckIn, setPendingCheckIn] = useState<Tote | null>(null)
  const scanInputRef = useRef<HTMLInputElement>(null)

  const totes = useMemo<Tote[]>(
    () => (getTotes.data as Tote[] | undefined) ?? [],
    [getTotes.data]
  )

  useEffect(() => {
    getTotes.trigger()
    scanInputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setCustomerFilter(searchParams.get('customer_id'))
  }, [searchParams])

  // Filter rows down to the scanned tote / selected customer when a filter is active
  const filteredTotes = useMemo(() => {
    let rows = totes
    if (customerFilter) rows = rows.filter(t => t.customer_id === customerFilter)
    if (toteFilter) rows = rows.filter(t => String(t.id) === toteFilter)
    return rows
  }, [totes, toteFilter, customerFilter])

  const customerFilterName = useMemo(() => {
    if (!customerFilter) return null
    return totes.find(t => t.customer_id === customerFilter)?.customer ?? customerFilter
  }, [customerFilter, totes])

  const clearCustomerFilter = useCallback(() => {
    setCustomerFilter(null)
    const next = new URLSearchParams(searchParams)
    next.delete('customer_id')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const handleScanFilter = useCallback(() => {
    const raw = scanValue.trim()
    if (!raw) {
      setToteFilter('')
      return
    }
    if (!/^\d+$/.test(raw)) {
      playSound('error')
      toast.error('Tote IDs are numeric. Please scan a valid tote.')
      return
    }
    const exists = totes.some(t => String(t.id) === raw)
    if (!exists) {
      playSound('error')
      toast.error(`Tote ${raw} not found`)
    } else {
      playSound('success')
    }
    setToteFilter(raw)
    setScanValue('')
    scanInputRef.current?.focus()
  }, [scanValue, totes])

  const clearFilter = useCallback(() => {
    setToteFilter('')
    setScanValue('')
    scanInputRef.current?.focus()
  }, [])

  const confirmCheckIn = useCallback(async () => {
    if (!pendingCheckIn) return
    const tote = pendingCheckIn
    setPendingCheckIn(null)
    try {
      await checkInTote.trigger({ tote_id: String(tote.id) })
      playSound('success')
      toast.success(`Tote ${tote.id} checked in`)
      getTotes.trigger(undefined, { skipCache: true })
    } catch (error) {
      playSound('error')
      toast.error(
        `Failed to check in tote: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }, [pendingCheckIn, checkInTote, getTotes])

  // No "Are you sure?": the toast's Undo (and Settings > Recently deleted)
  // puts the tote back.
  const deleteNow = useCallback(async (tote: Tote) => {
    try {
      const result = (await deleteTote.trigger({ tote_id: String(tote.id) })) as
        | { trash_id?: number | null }
        | undefined
      showUndo(`Tote ${tote.id} deleted`, result?.trash_id)
      getTotes.trigger(undefined, { skipCache: true })
    } catch (error) {
      playSound('error')
      toast.error(
        `Failed to delete tote: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }, [deleteTote, getTotes, showUndo])

  useOnRestored(['tote'], () => {
    getTotes.trigger(undefined, { skipCache: true })
  })

  const columns = useMemo<ColumnDef<Tote>[]>(
    () => [
      { accessorKey: 'id', header: 'Tote ID' },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) =>
          row.original.customer_id ? (
            <Badge variant="secondary">Checked Out</Badge>
          ) : (
            <Badge variant="outline">Available</Badge>
          ),
      },
      {
        accessorKey: 'customer',
        header: 'Checked Out To',
        cell: ({ row }) => row.original.customer || <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: 'shipping_address',
        header: 'Address',
        cell: ({ row }) => row.original.shipping_address || <span className="text-muted-foreground">—</span>,
      },
      {
        accessorKey: 'last_modified',
        header: 'Last Modified',
        cell: ({ row }) => formatDate(row.original.last_modified),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={!row.original.customer_id}
              onClick={() => setPendingCheckIn(row.original)}
            >
              <PackageOpen className="w-4 h-4 mr-1.5" />
              Check In
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Delete tote ${row.original.id}`}
              className="text-muted-foreground hover:text-destructive"
              onClick={() => void deleteNow(row.original)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ),
      },
    ],
    [deleteNow]
  )

  const table = useReactTable({
    data: filteredTotes,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  const checkedOutCount = totes.filter(t => t.customer_id).length

  return (
    <div className="p-4 sm:p-6 space-y-6 min-h-screen bg-background">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Totes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {totes.length} total · {checkedOutCount} checked out ·{' '}
            {totes.length - checkedOutCount} available
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => getTotes.trigger(undefined, { skipCache: true })}
          disabled={getTotes.loading}
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${getTotes.loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* ── Scan to filter ──────────────────────────────────────────────── */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-5 space-y-2 max-w-xl">
        <h2 className="text-base font-semibold">Scan a Tote to Filter</h2>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              ref={scanInputRef}
              value={scanValue}
              onChange={e => setScanValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleScanFilter() }}
              placeholder="Scan or type a tote ID…"
              autoComplete="off"
              className="pr-8"
            />
            {scanValue && (
              <button
                onClick={() => { setScanValue(''); scanInputRef.current?.focus() }}
                aria-label="Clear input"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button onClick={handleScanFilter}>Filter</Button>
        </div>
        {toteFilter && (
          <div className="flex items-center gap-2 pt-1">
            <Badge variant="secondary">Filtered to tote {toteFilter}</Badge>
            <button
              onClick={clearFilter}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear filter
            </button>
          </div>
        )}
        {customerFilter && (
          <div className="flex items-center gap-2 pt-1">
            <Badge variant="secondary">Totes for {customerFilterName}</Badge>
            <button
              onClick={clearCustomerFilter}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear filter
            </button>
          </div>
        )}
      </div>

      {/* ── Totes table ─────────────────────────────────────────────────── */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted() as string] ?? ''}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  {getTotes.loading ? 'Loading totes…' : 'No totes found.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Check-in confirmation ──────────────────────────────────────── */}
      <AlertDialog open={pendingCheckIn !== null} onOpenChange={open => { if (!open) setPendingCheckIn(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Check in tote {pendingCheckIn?.id}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear the customer association for tote {pendingCheckIn?.id}
              {pendingCheckIn?.customer ? ` (currently checked out to ${pendingCheckIn.customer})` : ''}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmCheckIn()}>Check In</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  )
}
